import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Card, CardContent, Grid, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import ChecklistIcon from '@mui/icons-material/Checklist'
import InventoryIcon from '@mui/icons-material/Inventory'
import { dcApi } from '../services/dc.api'
import { DCOrder } from '../types/dc.types'

const statusColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  confirmed: 'primary',
  ordered: 'info',
  partially_received: 'warning',
  received: 'secondary',
  allocated: 'success',
  completed: 'success'
}

export default function DCOrdersPage() {
  const navigate = useNavigate()
  const [page] = useState(1)

  const { data: ordersData, isLoading, error } = useQuery({
    queryKey: ['dc-orders', page],
    queryFn: () => dcApi.getOrders({ page, limit: 20 })
  })

  const { data: managedData } = useQuery({
    queryKey: ['dc-managed-items-count'],
    queryFn: () => dcApi.getManagedItems({ limit: 1 })
  })

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">Failed to load orders: {(error as Error).message}</Alert>
  }

  const orders = ordersData?.orders || []
  const managedCount = managedData?.pagination?.total || 0

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">DC Orders</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/orders/create')}
          disabled={managedCount === 0}
        >
          Create Order
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate('/orders/eligible')}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ChecklistIcon color="primary" fontSize="large" />
              <Box>
                <Typography variant="h6">Eligible Items</Typography>
                <Typography variant="body2" color="text.secondary">
                  Browse & add to DC management
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate('/orders/managed')}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <InventoryIcon color="secondary" fontSize="large" />
              <Box>
                <Typography variant="h6">{managedCount} Managed Items</Typography>
                <Typography variant="body2" color="text.secondary">
                  Items approved for DC ordering
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ShoppingCartIcon color="action" fontSize="large" />
              <Box>
                <Typography variant="h6">{ordersData?.pagination?.total || 0} Total Orders</Typography>
                <Typography variant="body2" color="text.secondary">
                  All DC orders
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Orders Table */}
      <Typography variant="h6" gutterBottom>Recent Orders</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order #</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Items</TableCell>
              <TableCell align="right">Distributors</TableCell>
              <TableCell align="right">Est. Cost</TableCell>
              <TableCell>Created By</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No orders yet. Add items to DC management first, then create an order.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order: DCOrder) => (
                <TableRow
                  key={order.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <TableCell>
                    <Typography fontWeight={600}>{order.order_number}</Typography>
                  </TableCell>
                  <TableCell>{order.order_date || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={order.status}
                      size="small"
                      color={statusColors[order.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">{order.total_items}</TableCell>
                  <TableCell align="right">{order.total_distributors}</TableCell>
                  <TableCell align="right">
                    Rs. {(order.total_estimated_cost || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {order.created_by || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
